import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { uploadReportAction } from "../../../store/actions/ReportAction";
import { showErrorAlert } from "../../../services/ErrorHandlingService";

const UploadReportModal = ({ show, onClose, patientId }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    reportTitle: "",
    reportDate: new Date().toISOString().split("T")[0],
    reportDescription: "",
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  // handle text change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // handle multiple file select
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  // remove file
  const removeFile = (index) => {
    const updated = [...selectedFiles];
    updated.splice(index, 1);
    setSelectedFiles(updated);
  };

  // submit
  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      showErrorAlert("Please select at least one file!");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      data.append("patient_id", patientId);
      data.append("report_title", formData.reportTitle);
      data.append("report_date", formData.reportDate);
      data.append("report_description", formData.reportDescription);

      // append multiple files
      selectedFiles.forEach((file) => {
        data.append("report_file[]", file);
      });

      const response = await dispatch(uploadReportAction(data));

      if (response?.status === 200) {
        handleClose();
      }
    } catch (error) {
      showErrorAlert("Failed to upload report!");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      reportTitle: "",
      reportDate: new Date().toISOString().split("T")[0],
      reportDescription: "",
    });
    setSelectedFiles([]);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Upload Report</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Title */}
          <Form.Group className="mb-3">
            <Form.Label>Report Title *</Form.Label>
            <Form.Control
              type="text"
              name="reportTitle"
              value={formData.reportTitle}
              onChange={handleChange}
              placeholder="Enter report title"
            />
          </Form.Group>

          {/* Date */}
          <Form.Group className="mb-3">
            <Form.Label>Report Date *</Form.Label>
            <Form.Control
              type="date"
              name="reportDate"
              value={formData.reportDate}
              onChange={handleChange}
            />
          </Form.Group>

          {/* File Upload */}
          <Form.Group className="mb-3">
            <Form.Label>Upload File(s) *</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
            />
          </Form.Group>

          {/* Selected file list */}
          {selectedFiles.length > 0 && (
            <div className="mb-3">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
                >
                  <span style={{ fontSize: "14px" }}>{file.name}</span>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => removeFile(index)}
                  >
                    ✕
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Description */}
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="reportDescription"
              value={formData.reportDescription}
              onChange={handleChange}
              placeholder="Optional description"
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>

        <Button variant="primary" disabled={loading} onClick={handleSubmit}>
          {loading ? "Uploading..." : "Upload"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadReportModal;